import { TableColumn } from '../model/common/column';
import { ControllerNote } from '../model/controllerNotes/controllerNote';
import { Entry } from '../model/controllerNotes/entry';
import { Schedule } from '../model/contacts/contact-list-schedule';
import moment from 'moment-timezone';
import { utc, tz } from 'moment';
import { AdditionalResourceList } from '../model/contacts/additional-resource-list';

// Works off of local machine time, confirmed with Garrett that this is how they want it to work
export function getCurrentShiftDate() {
  let now = new Date();
  if (now.getHours() >= 6) {
    return now;
  } else if (now.getHours() == 5 && now.getMinutes() >= 45) {
    return now;
  } else {
    // time is earlier than 5:45 AM, therefore the current shift date is the day before
    now.setDate(now.getDate() - 1);
    return now;
  }
}

export const categories = [
  { name: 'Compressor Station' },
  { name: 'M&R' },
  { name: 'Pipeline' },
  { name: 'External Caller' },
  { name: 'System' },
  { name: 'Valve' },
];

export const emptyNote: ControllerNote = {
  id: '1',
  category: 'M&R',
  AORId: '',
  AORName: '',
  facilityId: '',
  facilityName: '',
  facilityUID: '',
  contactId: '',
  contactName: '',
  state: '',
  county: '',
  township: '',
  city: '',
  noteType: '',
  maop: false,
  falseAlarm: false,
  abnormalOperation: false,
  addToTurnover: false,
  createdAt: new Date(),
  createdBy: '',
};

export const emptyEntry: Entry = {
  id: '1',
  controllerNotesId: '1',
  time: new Date(),
  user: '',
  note: '',
  createdBy: '',
  updater: '',
  createdAt: new Date(),
  updatedAt: new Date(),
  deletedAt: new Date(),
  version: -1,
};

export const emptySchedule: Schedule = {
  id: '',
  contactList: null,
  contact: null,
  utcStartTime: null,
  utcEndTime: null,
  startTime: new Date(),
  endTime: new Date(),
  timezone: '',
  isPrimary: false,
};

// Canada Gas has requested that we show controller notes from their AORs in different timezones
// instead of the computer viewing the note's timezone. We will use these hardcoded lists for now,
// but in the future we would like to adjust this to not be hardcoded
export const CANADA_EST_AORS = ['ML East', 'ML West'];
export const CANADA_MST_AORS = ['Central', 'CGL', 'East', 'North'];

// Helper function that takes in a date in UTC and makes a new date for that
// given timestamp in a different UTC offset & tricks the browser into showing that time
export function getDateInUTCOffset(
  utcDate: Date | string | null,
  utcOffset: number
) {
  if (isNullOrUndefined(utcDate)) {
    return null;
  }
  let utcMoment = moment.utc(utcDate);
  // Convert the created moment to the appropriate timezone
  // since we don't care about daylight savings this works fine
  const momentInTimezone = utcMoment.clone().utcOffset(utcOffset, false);
  // Format the converted timestamp out into a nicer human readable version
  const formattedInTimezone = momentInTimezone.format('YYYY/MM/DD HH:mm:ss');
  // Convert that previous timestamp back into a date object to trick the browser
  // this makes the browser display this time exactly, although the offset data on it
  // will be wrong, this is what is needed for the display
  return new Date(formattedInTimezone);
}

export enum SortDirection {
  ASCENDING = 1,
  DESCENDING = -1,
}
// Takes in 2 objects a and b, which share a property prop, that we want to sort on
// also accepts a sort order, by default this is 1 for ascending, but also accepts -1
// for descending
// Usage: objectArray.sort((a, b) => sortByProperty(a, b, 'propertyName', 1))
export function sortByProperty(
  a: any,
  b: any,
  prop: string,
  order: SortDirection = SortDirection.ASCENDING
) {
  if (a[prop] > b[prop]) {
    return 1 * order;
  } else if (a[prop] < b[prop]) {
    return -1 * order;
  } else {
    return 0;
  }
}

// Utility function that calls sortByProperty with the field 'name'
// Usage: objectArray.sort(sortByName);
export function sortByName(a: { name: string }, b: { name: string }) {
  return sortByProperty(a, b, 'name');
}

export function sortByAdditionalResourceListType(
  a: AdditionalResourceList,
  b: AdditionalResourceList
) {
  return sortByProperty(
    a.AdditionalResourceListType,
    b.AdditionalResourceListType,
    'name'
  );
}

// Convert UTC date to local timezone
// Returns null if no date provided
export const getCorrectedDate = (inputDate: string | Date | null) => {
  if (!inputDate) {
    return null;
  }

  if (inputDate instanceof Date) {
    return new Date(inputDate);
  }

  const offset = new Date().getTimezoneOffset();
  const date = new Date(inputDate);
  date.setMinutes(date.getMinutes() - offset);
  return date;
};

export const isNullOrUndefined = (input: any): boolean => {
  return input === null || input === undefined;
};

export const isNotNullOrUndefined = <T>(
  input: T | null | undefined
): input is T => {
  return input !== null && input !== undefined;
};

export const getDateInTimeZone = (
  inputDate: string | Date,
  timezone: string
) => {
  if (!inputDate) {
    return null;
  }

  const utcDate = utc(inputDate);
  const convertedDate = tz(utcDate, timezone).format('YYYY-MM-DDTHH:mm:ss');
  return new Date(convertedDate);
};

// This should return the current AOR time in a format that the user's browser will show it and think of it as
// in their timezone
export function getCurrentOffsetTime(utcOffset: number): Date | null {
  // This is reversed from what you expect, e.g. UTC-7 becomes Etc/GMT+7 due to POSIX conventions
  const tzName = `Etc/GMT${utcOffset > 0 ? '-' : '+'}${Math.abs(utcOffset)}`;
  const nowInOffset = moment.utc().tz(tzName);
  return new Date(nowInOffset.format('YYYY-MM-DDTHH:mm'));
}

// Helper function that takes in a date in the user's timezone and makes a new date for that
// given timestamp in a different UTC offset & tricks the browser into having that time
export function convertClientTimeToAORTime(
  date: Date | string | null,
  utcOffset: number
): Date | null {
  if (isNullOrUndefined(date)) {
    return null;
  }
  let passedInDateAsMoment = moment(date);

  // This is reversed from what you expect, e.g. UTC-7 becomes Etc/GMT+7 due to POSIX conventions
  const tzName = `Etc/GMT${utcOffset > 0 ? '-' : '+'}${Math.abs(utcOffset)}`;
  return moment
    .tz(passedInDateAsMoment.format('YYYY-MM-DDTHH:mm:ss'), tzName)
    .toDate();
}
